<script setup>
import { ref, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';
import { useAccount } from 'dashboard/composables/useAccount';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const { accountId } = useAccount();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const isRecording = ref(false);
const mediaRecorder = ref(null);
const audioChunks = ref([]);
const recordingDuration = ref(0);
const recordingInterval = ref(null);
const continuousMode = ref(false);
const currentStream = ref(null);

const formatMessagesForApi = () => {
  return messages.value.map(message => ({
    role: message.sender,
    content: message.content,
  }));
};

const resetConversation = () => {
  messages.value = [];
  newMessage.value = '';
};

const sendMessage = async (textContent = null) => {
  // Handle case where event object is passed instead of text
  if (textContent && typeof textContent !== 'string' && textContent.preventDefault) {
    textContent.preventDefault();
    textContent = null;
  }
  
  const messageToSend = textContent || newMessage.value.trim();
  
  if (!messageToSend || typeof messageToSend !== 'string') {
    return;
  }
  
  if (isLoading.value) {
    return;
  }

  const userMessage = {
    content: messageToSend,
    sender: 'user',
    timestamp: new Date().toISOString(),
  };
  messages.value.push(userMessage);
  newMessage.value = '';

  try {
    isLoading.value = true;
    const { data } = await CaptainAssistant.playground({
      assistantId,
      messageContent: messageToSend,
      messageHistory: formatMessagesForApi(),
    });

    const assistantMessage = {
      content: data.response,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      audioUrl: data.audio_url || null,
    };
    messages.value.push(assistantMessage);
    
    // If continuous mode is enabled, auto-start recording after response
    if (continuousMode.value && !isRecording.value) {
      setTimeout(() => {
        if (!isRecording.value && continuousMode.value) {
          startRecording();
        }
      }, 500);
    }
  } catch (error) {
    console.error('[CAPTAIN][Playground] Error getting assistant response:', error);
    // Remove the user message if the API call failed
    messages.value = messages.value.filter(msg => msg !== userMessage);
  } finally {
    isLoading.value = false;
  }
};

const startRecording = async () => {
  try {
    console.log('[CAPTAIN][Playground][DEBUG] Starting audio recording...');
    // Stop any existing stream first
    if (currentStream.value) {
      currentStream.value.getTracks().forEach(track => track.stop());
    }
    
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    currentStream.value = stream;
    console.log('[CAPTAIN][Playground][DEBUG] Microphone access granted, creating MediaRecorder...');
    
    mediaRecorder.value = new MediaRecorder(stream);
    audioChunks.value = [];
    recordingDuration.value = 0;

    mediaRecorder.value.ondataavailable = event => {
      if (event.data.size > 0) {
        console.log('[CAPTAIN][Playground][DEBUG] Audio chunk received, size:', event.data.size, 'bytes');
        audioChunks.value.push(event.data);
      }
    };

    mediaRecorder.value.onstop = async () => {
      console.log('[CAPTAIN][Playground][DEBUG] Recording stopped, creating blob from', audioChunks.value.length, 'chunks');
      const audioBlob = new Blob(audioChunks.value, { type: 'audio/webm' });
      console.log('[CAPTAIN][Playground][DEBUG] Audio blob created, total size:', audioBlob.size, 'bytes');
      await handleAudioRecording(audioBlob);
      
      // Only stop tracks if continuous mode is disabled
      if (!continuousMode.value) {
        if (currentStream.value) {
          currentStream.value.getTracks().forEach(track => track.stop());
          currentStream.value = null;
          console.log('[CAPTAIN][Playground][DEBUG] Microphone tracks stopped (continuous mode disabled)');
        }
      } else {
        console.log('[CAPTAIN][Playground][DEBUG] Continuous mode enabled, keeping microphone active');
      }
    };

    mediaRecorder.value.start();
    isRecording.value = true;
    console.log('[CAPTAIN][Playground][DEBUG] MediaRecorder started');
    
    // Start duration counter
    recordingInterval.value = setInterval(() => {
      recordingDuration.value += 1;
    }, 1000);
    console.log('[CAPTAIN][Playground][DEBUG] Recording duration counter started');
  } catch (error) {
    console.error('[CAPTAIN][Playground][ERROR] Error accessing microphone:', error);
    alert('Could not access microphone. Please check permissions.');
  }
};

const stopRecording = () => {
  console.log('[CAPTAIN][Playground][DEBUG] Stop recording called');
  if (mediaRecorder.value && isRecording.value) {
    console.log('[CAPTAIN][Playground][DEBUG] Stopping MediaRecorder...');
    mediaRecorder.value.stop();
    isRecording.value = false;
    if (recordingInterval.value) {
      clearInterval(recordingInterval.value);
      recordingInterval.value = null;
      console.log('[CAPTAIN][Playground][DEBUG] Recording interval cleared');
    }
  } else {
    console.warn('[CAPTAIN][Playground][WARN] Stop recording called but not recording');
  }
};

// Watch for continuous mode changes (handled by v-model)
watch(continuousMode, (newValue) => {
  console.log('[CAPTAIN][Playground][DEBUG] Continuous mode changed to:', newValue);
  
  if (!newValue) {
    // If disabling continuous mode, stop the stream if recording is not active
    if (!isRecording.value && currentStream.value) {
      currentStream.value.getTracks().forEach(track => track.stop());
      currentStream.value = null;
      console.log('[CAPTAIN][Playground][DEBUG] Stopped microphone stream (continuous mode disabled)');
    }
  } else if (!isRecording.value && !isLoading.value) {
    // If enabling continuous mode and not currently recording/loading, start recording
    startRecording();
  }
});

const formatDuration = seconds => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const handleAudioRecording = async audioBlob => {
  try {
    console.log('[CAPTAIN][Playground][DEBUG] Starting audio transcription...');
    console.log('[CAPTAIN][Playground][DEBUG] Audio blob size:', audioBlob.size, 'bytes');
    console.log('[CAPTAIN][Playground][DEBUG] Audio blob type:', audioBlob.type);
    console.log('[CAPTAIN][Playground][DEBUG] Assistant ID:', assistantId);
    
    isLoading.value = true;
    
    // Convert blob to file
    const audioFile = new File([audioBlob], 'recording.webm', {
      type: 'audio/webm',
    });
    console.log('[CAPTAIN][Playground][DEBUG] Audio file created:', {
      name: audioFile.name,
      size: audioFile.size,
      type: audioFile.type,
    });

    // Transcribe audio via playground endpoint
    const formData = new FormData();
    formData.append('audio', audioFile);
    console.log('[CAPTAIN][Playground][DEBUG] FormData created, calling transcribeAudio API...');

    const { data } = await CaptainAssistant.transcribeAudio({
      assistantId,
      audioFile: formData,
    });

    console.log('[CAPTAIN][Playground][DEBUG] Transcription response received:', data);
    console.log('[CAPTAIN][Playground][DEBUG] Response structure check - data.transcription:', data.transcription);
    console.log('[CAPTAIN][Playground][DEBUG] Full data object keys:', Object.keys(data));

    if (data.transcription) {
      console.log('[CAPTAIN][Playground][DEBUG] Transcription successful:', data.transcription);
      // Set loading to false before calling sendMessage, otherwise sendMessage will return early
      isLoading.value = false;
      console.log('[CAPTAIN][Playground][DEBUG] isLoading set to false, now calling sendMessage with transcription...');
      await sendMessage(data.transcription);
      console.log('[CAPTAIN][Playground][DEBUG] sendMessage call completed');
      
      // If continuous mode is enabled, auto-restart recording after transcription
      if (continuousMode.value && currentStream.value) {
        console.log('[CAPTAIN][Playground][DEBUG] Continuous mode enabled, preparing to restart recording...');
        // Wait a bit for the response to be processed
        setTimeout(() => {
          if (!isRecording.value && continuousMode.value && currentStream.value) {
            console.log('[CAPTAIN][Playground][DEBUG] Auto-restarting recording in continuous mode...');
            startRecording();
          }
        }, 1000);
      }
    } else {
      console.error('[CAPTAIN][Playground][ERROR] No transcription in response. Data:', data);
      isLoading.value = false;
      alert('Could not transcribe audio. Please try again.');
    }
  } catch (error) {
    console.error('[CAPTAIN][Playground][ERROR] Error transcribing audio:', error);
    console.error('[CAPTAIN][Playground][ERROR] Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      statusText: error.response?.statusText,
      config: error.config,
    });
    
    // Show detailed error message if available
    const errorMessage = error.response?.data?.error || error.response?.data?.debug?.message || error.message || 'Unknown error';
    const debugInfo = error.response?.data?.debug;
    
    if (debugInfo) {
      console.error('[CAPTAIN][Playground][ERROR] Debug info from server:', debugInfo);
      alert(`Error transcribing audio: ${errorMessage}\n\nCheck browser console for details.`);
    } else {
      console.error('[CAPTAIN][Playground][ERROR] Full error object:', JSON.stringify(error, null, 2));
      alert(`Error transcribing audio: ${errorMessage}\n\nCheck browser console for details.`);
    }
    isLoading.value = false;
  } finally {
    // Only set to false if we haven't already set it (in case of successful transcription)
    if (isLoading.value) {
      isLoading.value = false;
    }
    console.log('[CAPTAIN][Playground][DEBUG] Audio transcription attempt completed');
  }
};

onUnmounted(() => {
  if (recordingInterval.value) {
    clearInterval(recordingInterval.value);
  }
  if (mediaRecorder.value && isRecording.value) {
    mediaRecorder.value.stop();
  }
  if (currentStream.value) {
    currentStream.value.getTracks().forEach(track => track.stop());
    currentStream.value = null;
  }
  continuousMode.value = false;
});
</script>

<template>
  <div
    class="flex flex-col h-full rounded-lg p-4 border border-n-slate-4 text-n-slate-11"
  >
    <div class="mb-4">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <div class="flex items-center gap-2">
          <label
            class="flex items-center gap-2 text-sm text-n-slate-11 cursor-pointer"
            :class="{ 'text-n-iris-11': continuousMode }"
          >
            <input
              v-model="continuousMode"
              type="checkbox"
            />
            {{ t('CAPTAIN.PLAYGROUND.CONTINUOUS_MODE') }}
          </label>
          <NextButton
            ghost
            size="small"
            icon="i-lucide-rotate-ccw"
            @click="resetConversation"
          />
        </div>
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList :messages="messages" :is-loading="isLoading" />

    <div
      class="flex items-center bg-n-solid-1 outline outline-n-container rounded-lg p-3 gap-2"
    >
      <NextButton
        v-if="!isRecording"
        ghost
        size="small"
        icon="i-ph-microphone"
        :disabled="isLoading"
        @click="startRecording"
      />
      <div
        v-else
        class="flex items-center gap-2 px-2 text-sm text-n-ruby-9"
      >
        <span class="animate-pulse">●</span>
        <span>{{ formatDuration(recordingDuration) }}</span>
        <NextButton
          ghost
          size="small"
          icon="i-ph-stop"
          @click="stopRecording"
        />
      </div>
      <input
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0"
        :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        :disabled="isRecording || isLoading"
        @keyup.enter="() => sendMessage()"
      />
      <NextButton
        ghost
        size="small"
        :disabled="!newMessage.trim() || isRecording || isLoading"
        icon="i-lucide-send"
        @click="sendMessage"
      />
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('CAPTAIN.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
